import Fuse from 'fuse.js';
import { expandQuery } from '../TopNav/departmentSearch';
import apiClient from '../../lib/apiClient';
import logger from '../../lib/logger';

// Anything strictly above this is auto-accepted from fuzzy alone.
export const AUTO_ACCEPT_THRESHOLD = 0.85;
// Anything in this band gets an embedding rerank to disambiguate.
export const RERANK_MIN = 0.40;
export const RERANK_MAX = 0.85;
// How much of the combined score the embedding contributes.
const EMBEDDING_WEIGHT = 0.4;
const FUZZY_WEIGHT = 1 - EMBEDDING_WEIGHT;

// Tuned slightly differently from TopNav's dropdown search: the user is
// pasting Salesforce account names ("California Department of Technology"),
// not typing fragments, so we lean harder on full-name + acronym matches
// and de-emphasize search_keywords.
const FUSE_OPTIONS = {
  includeScore: true,
  threshold: 0.5,
  ignoreLocation: true,
  minMatchCharLength: 1,
  keys: [
    { name: 'acronym',           weight: 1.0 },
    { name: 'bu_code',           weight: 1.0 },
    { name: 'official_name',     weight: 0.9 },
    { name: 'common_name',       weight: 0.85 },
    { name: 'alternative_names', weight: 0.75 },
    { name: 'abbreviations',     weight: 0.7 },
    { name: 'nicknames',         weight: 0.6 },
    { name: 'search_keywords',   weight: 0.4 },
  ],
};

export function buildAccountMatcher(departments) {
  return new Fuse(departments || [], FUSE_OPTIONS);
}

export function matchOne(fuse, name, { limit = 5 } = {}) {
  if (!name) return [];
  const expanded = expandQuery(name);
  return fuse.search(expanded, { limit }).map(r => ({
    department: r.item,
    score: r.score,
    confidence: Math.max(0, Math.min(1, 1 - (r.score ?? 1))),
  }));
}

// Run matching for an array of {name, territory?} entries.
// Returns one row per input; auto-accepts confident matches (>= 0.85),
// otherwise leaves selection NULL so the operator confirms.
//
// Fuzzy-only; no network call. Use rerankAmbiguous() to layer
// embedding rerank on top for rows that need disambiguation.
export function matchAccounts(departments, entries) {
  const t0 = performance.now();
  const fuse = buildAccountMatcher(departments);
  const out = entries.map(({ name, territory }) => {
    const candidates = matchOne(fuse, name);
    const top = candidates[0];
    const autoAccept = top && top.confidence >= AUTO_ACCEPT_THRESHOLD;
    return {
      salesforce_account_name: name,
      salesforce_territory: territory || null,
      candidates,
      selected_department_id: autoAccept ? top.department.department_id : null,
      match_confidence: top ? Number(top.confidence.toFixed(3)) : null,
      match_source: autoAccept ? 'auto' : (top ? 'needs_confirmation' : 'unmatched'),
      // Set after rerank — combined fuzzy + embedding score, plus per-candidate
      // embedding similarities for tiebreaking on display.
      reranked: false,
    };
  });
  const auto = out.filter((r) => r.match_source === 'auto').length;
  const needs = out.filter((r) => r.match_source === 'needs_confirmation').length;
  const unmatched = out.filter((r) => r.match_source === 'unmatched').length;
  logger.accountLists.info('fuzzy match completed', {
    inputs: entries.length,
    departments: departments.length,
    auto,
    needs_confirmation: needs,
    unmatched,
    durationMs: Math.round(performance.now() - t0),
  });
  return out;
}

// For every row whose top fuzzy candidate sits in the ambiguous band,
// ask the backend to embed-and-rerank the candidate set. Reorders the
// `candidates` array in-place by combined score, and bumps any row
// whose combined top now clears AUTO_ACCEPT_THRESHOLD into auto-accept.
//
// Network failures degrade silently — we keep the fuzzy-only result.
// Concurrency capped to keep the rerank cheap when someone pastes 200 names.
export async function rerankAmbiguous(rows, { concurrency = 4, onProgress } = {}) {
  const tasks = rows
    .map((row, i) => ({ row, i }))
    .filter(({ row }) => {
      const top = row.candidates?.[0];
      if (!top) return false;
      return top.confidence >= RERANK_MIN && top.confidence < RERANK_MAX;
    });

  if (tasks.length === 0) return rows;

  const t0 = performance.now();
  logger.accountLists.info('rerank starting', { ambiguousRows: tasks.length, concurrency });
  let done = 0;
  let bumped = 0;
  const results = rows.slice();

  const worker = async () => {
    while (tasks.length > 0) {
      const next = tasks.shift();
      if (!next) break;
      const { row, i } = next;
      try {
        const candidateIds = row.candidates.map(c => c.department.department_id);
        const resp = await apiClient.post('/api/account-lists/match-rerank', {
          name: row.salesforce_account_name,
          candidate_department_ids: candidateIds,
        });
        const simByDept = new Map(
          (resp?.candidates || []).map(c => [c.department_id, c.embedding_similarity])
        );
        if (simByDept.size === 0) {
          done++; onProgress?.(done, tasks.length + done);
          continue;
        }

        // Blend fuzzy + embedding into a combined score per candidate.
        const blended = row.candidates.map(c => {
          const sim = simByDept.get(c.department.department_id) ?? 0;
          const combined = (FUZZY_WEIGHT * c.confidence) + (EMBEDDING_WEIGHT * sim);
          return { ...c, embedding_similarity: sim, combined };
        }).sort((a, b) => b.combined - a.combined);

        const top = blended[0];
        const autoAccept = top.combined >= AUTO_ACCEPT_THRESHOLD;
        if (autoAccept && row.match_source !== 'auto') bumped++;
        results[i] = {
          ...row,
          candidates: blended,
          selected_department_id: autoAccept ? top.department.department_id : row.selected_department_id,
          match_confidence: Number(top.combined.toFixed(3)),
          match_source: autoAccept
            ? 'auto_rerank'
            : (row.selected_department_id != null ? row.match_source : 'needs_confirmation'),
          reranked: true,
        };
      } catch (e) {
        // Silent degrade — keep fuzzy result.
        logger.accountLists.warn('rerank row failed, keeping fuzzy', {
          name: row.salesforce_account_name,
          error: e.message,
        });
      }
      done++;
      onProgress?.(done, tasks.length + done);
    }
  };

  await Promise.all(Array.from({ length: concurrency }, () => worker()));
  logger.accountLists.info('rerank completed', {
    bumped_to_auto: bumped,
    durationMs: Math.round(performance.now() - t0),
  });
  return results;
}
